/* eslint-disable */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import breakpoints from '../lib/breakpoints';
import { craftJsonClient, textClient } from '../lib/api';

gsap.registerPlugin(ScrollTrigger);

/**
 * Poor man's clamp
 *
 * @link https://www.ajaybadgujar.com/clamping-value-using-math-library-javascript/
 * @param val Value
 * @param min Minimum acceptable value
 * @param max Maximum acceptable value
 */
function clamp(val, min, max) {
  return Math.min(Math.max(min, val), max);
}

/**
 * Platform Animation module. Responsible for the scrolly video on the platform
 * page
 *
 * @param {HTMLElement} el
 * @constructor
 */
function PlatformAnimation(el) {
  let app = null;
  let PIXI = null;
  let timelines = [];
  let triggers = [];
  let sprite = null;
  let initialized = false;

  /**
   * All the image containers on the page
   *
   * @type {NodeListOf<Element>}
   */
  const imgContainers = el.querySelectorAll('[data-img-container]');

  /**
   * First image container, will take all the images
   *
   * @type {Element}
   */
  const firstContainer = imgContainers.item(0);
  const initialMarkup = firstContainer.innerHTML;

  /**
   * All the phrases
   * @type {NodeListOf<Element>}
   */
  const phrases = el.querySelectorAll('[data-phrase]');

  /**
   * All the phrase containers
   *
   * @type {NodeListOf<Element>}
   */
  const phraseContainers = el.querySelectorAll('[data-phrase-container]');

  /**
   * The last phrase, used to stop the scroll effect
   * @type {Element}
   */
  const lastPhrase = phrases.item(phrases.length - 1);

  /**
   * Fired each time the ScrollTrigger Updates
   * @param {float} progress Percentage complete between 0 and 1
   * @param {AnimatedSprite} images Image nodes
   */
  function updateAnimation(progress, sprite) {
    let frame = Math.floor(progress * sprite.totalFrames);
    frame = clamp(0, frame, sprite.totalFrames - 1);

    sprite.gotoAndStop(frame);
  }

  /**
   * Higher order function to pass images into our udpate animation function
   * @param {NodeList} images Image nodes
   * @returns {function(*): void}
   */
  function handleUpdate(sprite) {
    return function(self) {
      updateAnimation(self.progress, sprite);
    };
  }

  /**
   * Generate the loader markup
   *
   * @returns {string} Loader markup
   */
  function loader() {
    return `
<div class="absolute inset-0 flex flex-col justify-center items-center">
  <div class="loader" style="--loader-size: 64;">
    <div></div>
    <div></div>
  </div>
</div>
    `;
  }

  /**
   * Gets all our image frames as JSON
   *
   * @returns {Promise<void>}
   */
  function fetchFrames({ width = 600, height = 600 }) {
    firstContainer.innerHTML = loader();

    return craftJsonClient(
      `/actions/valo-module/animation?width=${width}&height=${height}`
    )
      .then(({ images }) => {
        return images;
      })
      .catch(err => {
        console.error(err);
        return [];
      });
  }

  /**
   * Hides our image containers
   */
  function hideContainers() {
    // Remove images from all the other containers
    imgContainers.forEach((el, index) => {
      if (index > 0) {
        el.style.display = 'none';
      }
    });
  }

  /**
   * Show the containers on destroy
   */
  function showContainers() {
    // eslint-disable-next-line no-console
    console.log('showing containers');
    imgContainers.forEach((el, index) => {
      el.style.display = 'block';
    });
  }

  function createPhraseTriggers() {
    phraseContainers.forEach((el, index) => {
      const phrase = el.querySelector('[data-phrase]');

      const trigger = ScrollTrigger.create({
        trigger: phrase,
        start: 'center center',
        pin: phrase.parentNode,
        pinReparent: true,
      });
      triggers.push(trigger);

      gsap.set(phrase, { alpha: 0 });
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          scrub: 1,
          start: 'center 90%',
          end: 'center 10%',
          trigger: el,
        },
      });
      tl.to(phrase, { alpha: 1, duration: 2 });
      tl.to(phrase, { alpha: 0 });

      timelines.push(tl);
    });
  }

  /**
   * Create our initial ScrollTrigger
   * @returns {ScrollTrigger} ScrollTrigger instance
   */
  function createTrigger(sprite) {
    const trigger = ScrollTrigger.create({
      trigger: firstContainer,
      start: `center center`,
      endTrigger: '#scrollTrigger',
      end: `bottom bottom`,
      pin: true,
      onUpdate: handleUpdate(sprite),
    });

    triggers.push(trigger);
    return trigger;
  }

  function createSprite(images, PIXI, app) {
    const srcs = images.map(image => image.src);
    const sprite = new PIXI.AnimatedSprite.fromImages(srcs);
    sprite.anchor.set(0.5);
    sprite.position.set(app.renderer.width / 2, app.renderer.height / 2);
    sprite.scale.set(0.6);
    return sprite;
  }

  function destroy() {
    timelines.forEach(tl => tl.kill());
    triggers.forEach(trigger => trigger.kill());
    showContainers();
    firstContainer.innerHTML = initialMarkup;
    app.stop();
  }

  function enable() {
    app.start();

    hideContainers();
    createTrigger(sprite);

    firstContainer.innerHTML = '';
    firstContainer.appendChild(app.view);
  }

  function create([PIXI, images]) {
    if (!app) {
      app = new PIXI.Application({
        resizeTo: firstContainer,
      });
    }

    if (!sprite) {
      sprite = createSprite(images, PIXI, app);
    }

    app.stage.addChild(sprite);
    app.view.classList.add('absolute', 'inset-0');
  }

  /**
   * Kick things off
   */
  function init() {
    if (!initialized) {
      const { width, height } = firstContainer.getBoundingClientRect();
      Promise.all([
        import(/* webpackChunkName: "pixi" */ 'pixi.js'),
        fetchFrames({ width, height }),
      ]).then(results => {
        create(results);
        enable();
        initialized = true;
      });
    } else {
      enable();
    }

    return destroy;
  }

  ScrollTrigger.matchMedia({
    [`(min-width: ${breakpoints.lg})`]: init,
  });
}

export default PlatformAnimation;
